import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

const EMPTY = { name: "", role: "", body_name: "", description: "", image: "", order: 0 };

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm(m); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/members/${editing.id}`, form);
      else await api.post("/members", form);
      toast.success("Zapisano");
      setOpen(false);
      load();
    } catch { toast.error("Błąd zapisu"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Usunąć tę osobę?")) return;
    await api.delete(`/members/${id}`);
    toast.success("Usunięto");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Organy Izby</h2>
          <p className="mt-1 text-sm text-muted-foreground">Zarządzaj składem organów</p>
        </div>
        <Button onClick={openNew} className="gap-1.5 bg-ztk-blue" data-testid="member-add"><Plus className="h-4 w-4" /> Dodaj osobę</Button>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {members.map((m) => (
          <div key={m.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-member-${m.id}`}>
            <h3 className="font-display font-semibold text-ztk-navy dark:text-white">{m.name}</h3>
            <p className="text-sm text-ztk-blue">{m.role}</p>
            <p className="mt-1 text-xs text-muted-foreground">{m.body_name}</p>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(m)} className="gap-1" data-testid={`member-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /> Edytuj</Button>
              <Button size="sm" variant="ghost" onClick={() => del(m.id)} className="gap-1 text-destructive" data-testid={`member-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edytuj osobę" : "Nowa osoba"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Imię i nazwisko / funkcja</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" className="mt-1.5" /></div>
            <div><Label>Stanowisko / rola</Label><Input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="member-role" className="mt-1.5" /></div>
            <div><Label>Organ (grupa)</Label><Input value={form.body_name} onChange={(e) => setForm({ ...form, body_name: e.target.value })} placeholder="np. Zarząd, Rada Izby" data-testid="member-body" className="mt-1.5" /></div>
            <div><Label>Opis</Label><div className="mt-1.5"><RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="member-desc" placeholder="Krótki opis funkcji..." /></div></div>
            <div><Label>Kolejność</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="mt-1.5" /></div>
            <ImageUpload value={form.image} multiple={false} label="Zdjęcie" onChange={(v) => setForm({ ...form, image: v })} />
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-ztk-blue" data-testid="member-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Zapisz</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
